import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { getQueryFn } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { StatsCard } from "@/components/ui/stats-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart3,
  BriefcaseBusiness,
  CalendarClock,
  FileBarChart2,
  Gauge,
  RefreshCw,
} from "lucide-react";
import { formatCurrency } from "@/lib/format";

interface RevenueByManagerRow {
  revenueOwnerUserId: string;
  ownerName: string;
  totalRevenue: number;
  invoiceCount: number;
}

interface RevenueMixRow {
  revenueCategory: string;
  totalRevenue: number;
  invoiceLineCount: number;
}

interface RevenueByServiceTypeRow {
  serviceTypeKey: string;
  serviceTypeLabel: string;
  totalRevenue: number;
  invoiceLineCount: number;
}

interface ProfitabilityRow {
  label: string;
  totalRevenue: number;
  totalCost: number;
  grossProfit: number;
  marginPercent: number;
}

interface UtilizationRow {
  id: string;
  userName: string;
  revenueMonth: string;
  scheduledMinutes: number;
  utilizedMinutes: number;
  billableMinutes: number;
  utilizationRate: number;
  billableUtilizationRate: number;
}

function buildRangeQuery(startDate: string, endDate: string) {
  return `startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
}

function minutesToHours(minutes: number) {
  return (minutes / 60).toFixed(1);
}

export default function ReportingPage() {
  const [startDate, setStartDate] = useState(
    format(startOfMonth(new Date()), "yyyy-MM-dd"),
  );
  const [endDate, setEndDate] = useState(
    format(endOfMonth(new Date()), "yyyy-MM-dd"),
  );
  const rangeQuery = buildRangeQuery(startDate, endDate);

  const revenueByManager = useQuery<RevenueByManagerRow[]>({
    queryKey: [`/api/reports/revenue-by-account-manager?${rangeQuery}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const recurringVsOneOff = useQuery<RevenueMixRow[]>({
    queryKey: [`/api/reports/recurring-vs-oneoff?${rangeQuery}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const revenueByServiceType = useQuery<RevenueByServiceTypeRow[]>({
    queryKey: [`/api/reports/revenue-by-service-type?${rangeQuery}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const profitability = useQuery<ProfitabilityRow[]>({
    queryKey: [`/api/reports/profitability?${rangeQuery}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const utilization = useQuery<UtilizationRow[]>({
    queryKey: [`/api/reports/utilization?revenueMonth=${startDate.slice(0, 7)}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const isLoading =
    revenueByManager.isLoading ||
    recurringVsOneOff.isLoading ||
    revenueByServiceType.isLoading ||
    profitability.isLoading ||
    utilization.isLoading;

  const totals = useMemo(() => {
    const mix = recurringVsOneOff.data ?? [];
    const totalRevenue = mix.reduce((sum, row) => sum + row.totalRevenue, 0);
    const recurringRevenue =
      mix.find((row) => row.revenueCategory === "recurring")?.totalRevenue ?? 0;
    const projectRevenue = mix
      .filter((row) => row.revenueCategory !== "recurring")
      .reduce((sum, row) => sum + row.totalRevenue, 0);
    const averageUtilization =
      (utilization.data ?? []).length > 0
        ? (utilization.data ?? []).reduce((sum, row) => sum + row.utilizationRate, 0) /
          (utilization.data ?? []).length
        : 0;

    return {
      totalRevenue,
      recurringRevenue,
      projectRevenue,
      averageUtilization,
    };
  }, [recurringVsOneOff.data, utilization.data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reporting"
        description="Revenue mix, service type performance, profitability, and utilization."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              className="w-[170px]"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              className="w-[170px]"
            />
            <Button
              variant="outline"
              onClick={() => {
                revenueByManager.refetch();
                recurringVsOneOff.refetch();
                revenueByServiceType.refetch();
                profitability.refetch();
                utilization.refetch();
              }}
              disabled={isLoading}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-[120px] rounded-lg" />
          ))
        ) : (
          <>
            <StatsCard
              title="Total Revenue"
              value={formatCurrency(totals.totalRevenue)}
              description={`${startDate} to ${endDate}`}
              icon={FileBarChart2}
            />
            <StatsCard
              title="Recurring Revenue"
              value={formatCurrency(totals.recurringRevenue)}
              description="Recurring invoice lines"
              icon={BriefcaseBusiness}
            />
            <StatsCard
              title="One-Off + Project"
              value={formatCurrency(totals.projectRevenue)}
              description="Non-recurring revenue"
              icon={BarChart3}
            />
            <StatsCard
              title="Avg Utilization"
              value={`${totals.averageUtilization.toFixed(1)}%`}
              description={`For ${startDate.slice(0, 7)}`}
              icon={Gauge}
            />
          </>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">
              Revenue By Account Manager
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueByManager.isLoading ? (
              <Skeleton className="h-[220px] rounded-lg" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Owner</TableHead>
                    <TableHead>Invoices</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(revenueByManager.data ?? []).map((row) => (
                    <TableRow key={row.revenueOwnerUserId}>
                      <TableCell className="font-medium">{row.ownerName}</TableCell>
                      <TableCell>{row.invoiceCount}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.totalRevenue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">
              Recurring Vs One-Off
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recurringVsOneOff.isLoading ? (
              <Skeleton className="h-[220px] rounded-lg" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Lines</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(recurringVsOneOff.data ?? []).map((row) => (
                    <TableRow key={row.revenueCategory}>
                      <TableCell className="font-medium capitalize">
                        {row.revenueCategory.replace(/_/g, " ")}
                      </TableCell>
                      <TableCell>{row.invoiceLineCount}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.totalRevenue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">
              Revenue By Service Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueByServiceType.isLoading ? (
              <Skeleton className="h-[220px] rounded-lg" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service Type</TableHead>
                    <TableHead>Lines</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(revenueByServiceType.data ?? []).map((row) => (
                    <TableRow key={row.serviceTypeKey}>
                      <TableCell className="font-medium">{row.serviceTypeLabel}</TableCell>
                      <TableCell>{row.invoiceLineCount}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.totalRevenue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">Profitability</CardTitle>
          </CardHeader>
          <CardContent>
            {profitability.isLoading ? (
              <Skeleton className="h-[220px] rounded-lg" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bucket</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Gross Profit</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(profitability.data ?? []).map((row) => (
                    <TableRow key={row.label}>
                      <TableCell className="font-medium">{row.label}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.totalRevenue)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.totalCost)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.grossProfit)}
                      </TableCell>
                      <TableCell className="text-right">{row.marginPercent}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Utilization</CardTitle>
        </CardHeader>
        <CardContent>
          {utilization.isLoading ? (
            <Skeleton className="h-[220px] rounded-lg" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team Member</TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Scheduled</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Billable</TableHead>
                  <TableHead className="text-right">Utilization</TableHead>
                  <TableHead className="text-right">Billable Utilization</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(utilization.data ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.userName}</TableCell>
                    <TableCell>{row.revenueMonth}</TableCell>
                    <TableCell className="text-right">
                      {minutesToHours(row.scheduledMinutes)}h
                    </TableCell>
                    <TableCell className="text-right">
                      {minutesToHours(row.utilizedMinutes)}h
                    </TableCell>
                    <TableCell className="text-right">
                      {minutesToHours(row.billableMinutes)}h
                    </TableCell>
                    <TableCell className="text-right">{row.utilizationRate}%</TableCell>
                    <TableCell className="text-right">
                      {row.billableUtilizationRate}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!utilization.isLoading && !(utilization.data ?? []).length && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <CalendarClock className="h-4 w-4" />
              No utilization snapshots found for {startDate.slice(0, 7)}.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
